import React, { useState, useEffect, useRef } from 'react';
import './GardenJournal.css';
import './Buttons.css';
import './Instructions.css';
import './MatrixGrid.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import JournalInstructions from './Utilities/JournalInstructions';
import api from '../api';

const journalFields = [
  'planted', 'plantingDate', 'method', 'amount', 
  'seedDepth', 'seedSpace', 'rowSpace', 'soilTemperature', 'dailyMinTemp', 
  'dailyMaxTemp','plantingNotes', 'germinationDate', 'germinationNotes',
  'thinningDate', 'thinningOut', 'thinningNotes', 'transplanted',
  'transplantDate', 'transplantAmount', 'transplantNote', 'harvested',
  'harvestDate', 'harvestAmount','harvestNotes' 
];

const journalFieldLabels = {
  planted: 'Planted', plantingDate: 'Date Planted', method: 'Planting Method',
  amount: 'Amount Planted', seedDepth: 'Seed Depth', seedSpace: 'Seed Spacing', 
  rowSpace: 'Row Spacing', soilTemperature: 'Soil Temp', dailyMinTemp: 'Min Temp', 
  dailyMaxTemp: 'Max Temp', plantingNotes: 'Planting Notes',
  germinationDate: 'Date Germinated', germinationNotes: 'Germination Notes', 
  thinningDate: 'Date Thinned', thinningOut: 'Thinned To', thinningNotes: 'Thinning Notes', transplanted: 'Transplanted',
  transplantDate: 'Date Transplanted', transplantAmount: 'Amount Transplanted', transplantNote: 'Transplant Notes', harvested: 'Harvested',
  harvestDate: 'Date Harvested', harvestAmount: 'Amount Harvested', harvestNotes: 'Harvest Notes'
};

const journalFieldHints = {
    planted: 'Has this crop been planted or not.',
  plantingDate: 'The day you planted this crop. Helps anchor seasonal timing and future reminders.',
  plantVariety: 'The specific variety name (e.g., Chioggia Beet). Useful for tracking performance year over year.',
  method: 'How you planted: Direct Sow, Start Indoors, or Transplant. Helps tailor future instructions.',
  amountType: 'Unit of planting: seeds, feet, plants, etc. Used to interpret amount planted.',
  amount: 'How much you planted, based on your garden and family size. Helps forecast yield.',
  gardenType: 'Where you planted: with regards to sunlight exposure (eg. full sunlight, partial sunlight, shaddy).',
  seedDepth: 'How deep you planted the seeds. Affects germination success and early growth.',
  seedSpace: 'Spacing between seeds. Helps avoid crowding and supports healthy development.',
  rowSpace: 'Spacing between rows. Affects airflow, layout, and disease prevention.',
    germinationDate: 'When you first saw sprouts. Helps track timing and validate planting conditions.',
  harvested: 'Has this crop been harvested or not.',
  harvestDate: 'When you harvested. Anchors future reminders and helps forecast maturity.',
  harvestNotes: 'Observations on yield, flavor, timing, or success. Useful for future planning.',
  transplanted: 'Has this crop been transplanted or not.',
  thinningDate: 'When you thinned the crop. Helps schedule thinning tasks next season.',
  thinningNotes: 'Why or how you thinned. Useful for adjusting spacing or improving airflow.',
  plantingNotes: 'Any observations or reminders you want to carry into next year. Personal and teachable.'
};

const GardenJournal = ({ currentGardener, isDemoMode }) => {
  const [cropViewMatrix, setCropViewMatrix] = useState({});
  const [yearViewMatrix, setYearViewMatrix] = useState({});
  const [visibleStart, setVisibleStart] = useState(0);
  const [visibleCount, setVisibleCount] = useState(5);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isYearView, setIsYearView] = useState(false);
  const [selectedCropLabel, setSelectedCropLabel] = useState(null);
  const [dirtyCells, setDirtyCells] = useState({});
  const matrixRef = useRef(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [journalMessage, setJournalMessage] = useState(null);
  const [journalMessageRow, setJournalMessageRow] = useState(null);
  const [rebuildPlanShown, setRebuildPlanShown] = useState(false);
  const [rebuildMessage, setRebuildMessage] = useState(null);

  const [visibleColumns, setVisibleColumns] = useState({
  amount: true,
  seedDepth: true,
  seedSpace: true,
  rowSpace: true,
  soilTemperature: true,
  dailyMinTemp: true,
  dailyMaxTemp: true,
  plantingNotes: true,

  germinationNotes: true,

  thinningOut: true,
  thinningNotes: true,

  transplantAmount: true,
  transplantNote: true,

  harvestAmount: true,
  harvestNotes: true
});

  const visibleFields = journalFields.slice(visibleStart, visibleStart + visibleCount);
  const activeMatrix = isYearView ? yearViewMatrix : cropViewMatrix;

  const buildCropViewMatrix = (data) => {
    const matrix = {};
    data.forEach(entry => {
      const cropKey = `${entry.cropLabel}-${entry.journalInfoID}`;
      const year = selectedYear;
      if (!matrix[cropKey]) matrix[cropKey] = {};
      matrix[cropKey][year] = {
        journalInfoID: entry.journalInfoID || null,
        plantID: entry.plantID || null,
        gardenYear: entry.gardenYear || parseInt(year),
        userPlantID: entry.userPlant || null,
        cropLabel:entry.cropLabel
      };
      journalFields.forEach(field => {
        matrix[cropKey][year][field] = entry[field] || '';
      });
    });
    return matrix;
  };

  const buildYearViewMatrix = (data) => {
    const matrix = {};
    data.forEach(entry => {
        const cropKey = `${entry.cropLabel}-${entry.journalInfoID}`;
        const year = entry.gardenYear;
      if (!matrix[cropKey]) matrix[cropKey] = {};
      matrix[cropKey][year] = {
        journalInfoID: entry.journalInfoID || null,
        plantID: entry.plantID || null,
        gardenYear: entry.gardenYear || parseInt(year),
          userPlantID: entry.userPlant || null,
          cropLabel: entry.cropLabel
      };
      journalFields.forEach(field => {
        matrix[cropKey][year][field] = entry[field] || '';
      });
    });
    return matrix;
  };

const topScrollRef = useRef(null);
const topInnerRef = useRef(null);

useEffect(() => {
  const top = topScrollRef.current;
  const wrapper = matrixRef.current; // contains .matrix-grid
  const grid = wrapper?.querySelector('.matrix-grid');

  if (!top || !grid) return;

  // Match top spacer width to scrollable content
  const topInner = topInnerRef.current;
  if (topInner) topInner.style.width = grid.scrollWidth + 'px';

  let isSyncingTop = false;
  let isSyncingBottom = false;

  const onTopScroll = () => {
    if (isSyncingTop) return;
    isSyncingBottom = true; // prevent feedback loop
    grid.scrollLeft = top.scrollLeft;
    isSyncingBottom = false;
  };

  const onGridScroll = () => {
    if (isSyncingBottom) return;
    isSyncingTop = true; // prevent feedback loop
    top.scrollLeft = grid.scrollLeft;
    isSyncingTop = false;
  };

  top.addEventListener('scroll', onTopScroll);
  grid.addEventListener('scroll', onGridScroll);

  return () => {
    top.removeEventListener('scroll', onTopScroll);
    grid.removeEventListener('scroll', onGridScroll);
  };
}, [activeMatrix]);

const formatDate = (dateString) => {
  if (!dateString) return '';
  return dateString.split('T')[0]; // prints YYYY-MM-DD exactly as stored
};

const toLocalDate = (ymd) => {
  if (!ymd) return null;
  if (ymd.includes('T')) ymd = ymd.split('T')[0]; // strip time if backend added it
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return null; // prevents "Invalid time value"
  return new Date(y, m - 1, d);
};

useEffect(() => {
        if (!currentGardener?.id) {
            setCropViewMatrix({});
            setYearViewMatrix({});
            setSelectedCropLabel(null);
            setDirtyCells({});
            return;
}

    const fetchVisibility = async () => {
        try {
            const settingsResponse = await api.get('/journal/journalVisible', {
                params: { gardenerID: currentGardener.id }
            });

            const s = settingsResponse.data || {};
            console.log('settingsResponse.data: ', s);

            setVisibleColumns({
                amount: !!s.askAmount,
                seedDepth: !!s.askSeedDepth,
                seedSpace: !!s.askSeedSpace,
                rowSpace: !!s.askRowSpace,
                soilTemperature: !!s.askSoilTemperature,
                dailyMinTemp: !!s.askDailyMinTemp,
                dailyMaxTemp: !!s.askDailyMaxTemp,

                plantingNotes: !!s.askPlantingNotes,
                germinationNotes: !!s.askGerminationNotes,

                thinningOut: !!s.askThinningOut,
                thinningNotes: !!s.askThinningNotes,

                transplantAmount: !!s.askTransplantAmount,
                transplantNote: !!s.askTransplantNote,

                harvestAmount: !!s.askHarvestAmount,
                harvestNotes: !!s.askHarvestNotes,
                harvestDate: !!s.askHarvestDate
            });
        } catch (err) {
            console.error('Error fetching visibility settings:', err);
        }
    };


    const fetchCropView = async () => {
        if (!currentGardener?.id) return;
        try {
            setCropViewMatrix({}); // Clear old data immediately
            setYearViewMatrix({});
            const response = await api.get(`/journal/journalInfo`, {
                params: {
                    gardenerID: currentGardener.id,
                    gardenYear: selectedYear
                }
        });
        const data = response.data;
        setCropViewMatrix(buildCropViewMatrix(data));
        setIsYearView(false);
        setSelectedCropLabel(null);
        setDirtyCells({});
    } catch (err) {
      console.error('Error fetching crop view:', err);
    }
  };

  fetchVisibility();
  fetchCropView();

}, [currentGardener, selectedYear, isDemoMode]);

  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      for (let entry of entries) {
        const containerWidth = entry.contentRect.width;
        const sidebarWidth = 260;
        const columnWidth = 160;
        const availableWidth = containerWidth - sidebarWidth;
        const count = Math.floor(availableWidth / columnWidth);
        setVisibleCount(Math.max(1, Math.min(count, journalFields.length)));
      }
    });
    if (matrixRef.current) observer.observe(matrixRef.current);
    return () => observer.disconnect();
  }, []);

  const handleCellChange = (crop, year, field, value) => {
    setCropViewMatrix(prev => ({
      ...prev,
      [crop]: {
        ...prev[crop],
        [year]: {
          ...prev[crop][year],
          [field]: value
        }
      }
    }));
    setDirtyCells(prev => ({
      ...prev,
      [`${crop}|${year}|${field}`]: true
    }));
  };

const handleCropClick = async (cropLabel) => {
    console.log("CLICKED:", cropLabel);
  const plantID = cropViewMatrix[cropLabel]?.[selectedYear]?.plantID;
  console.log("plantid: ", plantID);
  console.log("cropviewmatrix: ", cropViewMatrix);
  if (!plantID || !currentGardener?.id) return;

  try {
    const response = await api.get('/journal/journalInfo/cropHistory', {
      params: {
        gardenerID: currentGardener.id,
        plantID: plantID
      }
    });

      const data = response.data;

      const matrix = buildYearViewMatrix(data);

      setYearViewMatrix(matrix);
      setSelectedCropLabel(cropLabel);
    setIsYearView(true);
  } catch (err) {
    console.error('Error fetching year view:', err);
  }
};

function checkJournalRules(entry, field, newValue) {
  const toDate = (d) => (d ? new Date(d) : null);

  const planting = entry.plantingDate;
  const germination = entry.germinationDate;

  const p = toDate(planting);
  const g = toDate(germination);
  const n = toDate(newValue);

  // RULE 1: Must have a variety
  if (!entry.userPlantID) {
    return "You must create a variety first, then press Rebuild My Garden Plan.";
  }

  // RULE 2: Germination requires planting
  if (field === "germinationDate") {
    if (!p) {
      return "You must enter a planting date before germination.";
    }
    if (n < p) {
      return "Germination cannot occur before planting.";
    }
  }

  // More rules will go here later...

  return null; // valid
}

// This is used for messages while editing the matrix... the message will appear on the row
function showJournalMessage(rowKey, message) {
  setJournalMessage(message);
  setJournalMessageRow(rowKey);

  setTimeout(() => {
    setJournalMessage(null);
    setJournalMessageRow(null);
  }, 4000);
    }

//This is used for the message to rebuild the plan... want it to appear only once if they toggle or press submit changes button
    function showRebuildMessage(message) {
        setRebuildMessage(message);
        setTimeout(() => {
            setRebuildMessage(null);
        }, 4000);
    }

const handleSubmitChanges = async () => {
  console.log(' Submit button clicked');

  const token = localStorage.getItem('token');
  if (!token || !currentGardener?.id) {
    console.warn(' Missing token or gardenerID');
    return;
  }

  console.log(' Dirty cells:', dirtyCells);

  const updatesMap = {};

  Object.keys(dirtyCells).forEach(key => {
    const [crop, year, field] = key.split('|');
    const entry = cropViewMatrix[crop]?.[year];
    if (!entry) {
      console.warn(` No matrix entry for ${crop}|${year}`);
      return;
    }

    console.log(` Building update for ${crop}|${year}|${field}:`, entry[field]);

    const updateKey = `${crop}|${year}`;
    if (!updatesMap[updateKey]) {
      updatesMap[updateKey] = {
        journalInfoID: entry.journalInfoID || null,
        gardenerID: currentGardener.id,
        plantID: entry.plantID, 
        userPlantID: entry.userPlantID,
        gardenYear: parseInt(year),
        fields: {}
      };
    }

    updatesMap[updateKey].fields[field] = entry[field];
  });

  const updates = Object.values(updatesMap);
  console.log(' Final updates array:', updates);

  if (updates.length === 0) {
    console.warn(' No changes detected. Nothing to submit.');
    return;
  }

  try {
    const response = await api.post('/journal/journalInfo/batchUpdate', {
      gardenerID: currentGardener.id,
      updates
    });

    console.log('Journal updates submitted successfully:', response.data);
      setDirtyCells({});

      if (!rebuildPlanShown) {
          showRebuildMessage(
              "Some changes may impact your garden plan. Rebuild your plan now or changes will be reflected tomorrow."
          );
          setRebuildPlanShown(true);
      }

  } catch (err) {
    console.error('Error submitting journal updates:', err);
  }
};

return (
    <section className="garden-journal-wrapper">
        <div className="gj-banner-greeting">
            <h2>Garden Journal</h2>
                <button
                    type="button"
                    className="button"
                    onClick={() => setShowInstructions(prev => !prev)}
                    aria-expanded={showInstructions}
                    aria-controls="journal-instructions"
                    >
                    {showInstructions ? 'Hide Help' : 'Show Help'}
                </button>

                    {showInstructions && (
                    <div className="instructions-panel" id="journal-instructions">
                         <JournalInstructions />
                    </div>
            )}
            {rebuildMessage && (
                <div className="gj-toast">
                    {rebuildMessage}
                </div>
            )}

               <p className="gj-planner-subheading">

                {isYearView ? (
                    (() => {
                        const parts = selectedCropLabel?.split(' - ') || [];
                        const variety = parts[0]?.trim();
                        const plantDate = parts[1]?.trim();
                        const labelparts = plantDate?.split('-') || [];
                        const plant = labelparts[0]?.trim();

                        return <>Viewing history for <strong>{`${plant}`}</strong> across all years.</>;
                    })()
                ) : (
                    <>This matrix shows your planted crops and journal details for <strong>{selectedYear}</strong>. Switch years to revisit past gardens or plan ahead.</>
                )}
            </p>

            {!isYearView && (
                <div className="gj-year-selector">
                    <label htmlFor="year-select">Viewing year:</label>
                    <select
                        id="year-select"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    >
                        {Array.from({ length: 11 }, (_, i) => {
                            const year = new Date().getFullYear() - 10 + i;
                            return (
                                <option key={year} value={year}>{year}</option>
                            );
                        })}
                    </select>
                </div>
            )}
        </div>
{journalMessage && (
    <div className="gj-toast">
        {journalMessage}
    </div>
)}

        <div className="gj-matrix-nav">
            <button 
                className="button" 
                onClick={handleSubmitChanges}
                disabled={isDemoMode}
             >
                Submit Changes
            </button>

            {isDemoMode && (
                <p className="demo-hint">Submitting changes is disabled in demo mode.</p>
            )}

            {isYearView && (
                <button className="button" onClick={() => {
                    setIsYearView(false);
                    setSelectedCropLabel(null);
                }}>
                    Return to full matrix
                </button>
            )}
        </div>
        
        <details className="column-selector">
        <summary className="column-selector-summary">
            Hide/Show Questions
        </summary>

        <div className="column-selector-panel">
            {Object.keys(visibleColumns).map(field => (
            <label key={field} style={{ display: 'block', margin: '6px 0' }}>
                <input
                type="checkbox"
                checked={visibleColumns[field]}
                onChange={() => {
                    const newValue = !visibleColumns[field];

                    setVisibleColumns(prev => ({
                        ...prev,
                        [field]: newValue
                    }));

                    api.post('/journal/journalVisible/updateFlag', {
                        gardenerID: currentGardener.id,
                        field,
                        value: newValue ? 1 : 0
                    });

                    if (!rebuildPlanShown) {
                        showRebuildMessage(
                            "Some changes may impact your garden plan. Rebuild your plan now or changes will be reflected tomorrow."
                        );
                        setRebuildPlanShown(true);
                    }
                }}
                />
                {journalFieldLabels[field] || field}
            </label>
            ))}
        </div>
        </details>


        <div className="scroll-container">

        <div className="matrix-scroll-top" ref={topScrollRef}>
            <div className="matrix-scroll-top-inner" ref={topInnerRef}></div>
        </div>

        <div className="matrix-scroll-wrapper" 
             ref={matrixRef}         >
            <div className="matrix-grid">

                <div className="matrix-columns gj-planner-header-row">
                        <div className="matrix-column gj-crop-column gj-banner-highlight">
                            Crop
                        </div>

                        {isYearView && (
                            <div className="matrix-column gj-crop-column gj-banner-highlight">
                                Year
                            </div>
                        )}

{journalFields.filter(field => visibleColumns[field] !== false).map(field => (
                         <div key={field} className={`matrix-column gj-month-column gj-banner-highlight gj-field-${field}`}>
                                <h3
                                    className="gj-banner-section-title"
                                    title={journalFieldHints[field] || ''}
                                    >
                                    {journalFieldLabels[field] || field}
                                </h3>
                            </div>
                        ))}
                </div>


                {Object.keys(activeMatrix).map(label =>
                     Object.keys(activeMatrix[label]).map(year => {
                        const parts = label.split('-');
                        const rawPlant = parts[0]?.trim();
                        const rawVariety = parts[1]?.trim();

                        const plant = rawVariety?.toLowerCase() === 'null' ? rawPlant : rawVariety;
                        const variety = rawVariety?.toLowerCase() === 'null' ? '' : rawPlant;

                        return (
                            <div key={`${label}-${year}`} className="matrix-columns gj-planner-row">
                            {journalMessageRow === `${label}|${year}` && (
                                <div className="gj-inline-warning-row">
                                    <div className="gj-inline-warning-box">
                                    {journalMessage}
                                    </div>
                                </div>
                                )}

                                {/* Column 1: CropLabel */}
                                <div className="matrix-column gj-crop-column gj-banner-highlight">
                                    <span
                                        className="gj-crop-label"
                                        onClick={() => !isYearView && handleCropClick(label)}
                                    >
                                        {activeMatrix[label][year].cropLabel}
                                    </span>
                                </div>

                                {/* Column 2: Year */}
                                {isYearView && (
                                    <div className="matrix-column gj-crop-column gj-banner-highlight">
                                        {year}
                                    </div>
                                )}

                                {journalFields
                                .filter(field => visibleColumns[field] !== false)
                                .map(field => {
                                    let value = activeMatrix[label][year]?.[field] || '';
                                    const isDateField = ['plantingDate', 'germinationDate', 'harvestDate', 'thinningDate', 'transplantDate'].includes(field);

                                    // Inject variety from cropKey if field is 'plantVariety' and value is empty
                                    if (field === 'plantVariety' && !value) {
                                        value = variety;
                                    }

                                    //rule always disable planted field... must be entered through task
                                    const isPlantedField = field === "planted";

                                    //rule always disable transplanted field... must be entered through task
                                    const isTransplantedField = field === "transplanted";

                                    //rule always disable harvested field... must be entered through task
                                    const isHarvestedField = field === "harvested";

                                    //rule always disable method field... it is set by the planting window event
                                    const isMethodField = field === "method";

                                    //rule disable row if plantingDate is null... must initiate through task
                                    const isCurrentYear = parseInt(year) === new Date().getFullYear();
                                    const disableRow = !isCurrentYear || !activeMatrix[label][year]?.plantingDate;

                                    // Display journalInfo.planted as plain text
                                    if (field === 'planted') {
                                        let display = '';
                                        const normalized = String(value).toLowerCase();

                                        if (normalized === 'yes') value = 'Planted';
                                        else if (normalized === 'no') value = 'Not Planted';
                                    }
                                    // Display journalInfo.transplanted as plain text
                                    if (field === 'transplanted') {
                                        let display = '';
                                        const normalized = String(value).toLowerCase();

                                        if (normalized === 'yes') value = 'Transplanted';
                                        else if (normalized === 'no') value = 'Not Transplanted';
                                    }

                                    // Display journalInfo.harvested as plain text
                                    if (field === 'harvested') {
                                        let display = '';
                                        const normalized = String(value).toLowerCase();

                                        if (normalized === 'yes') value = 'Harvested';
                                        else if (normalized === 'no') value = 'Not Harvested';
                                    }

                                        return (
                                            <div key={field} className={`matrix-column gj-month-column gj-banner-highlight gj-field-${field}`}>
                                                {isDateField ? (
                                                    <DatePicker
                                                        selected={value ? toLocalDate(value) : null}
                                                        onChange={(date) => {
                                                            const entry = activeMatrix[label][year];
                                                            const rowKey = `${label}|${year}`;
                                                            const isoDate = date.toISOString().split('T')[0];

                                                            // RULE 1: entering a plantingDate forces planted = "yes"
                                                            if (field === 'plantingDate') {
                                                                handleCellChange(label, year, 'planted', 'Yes');
                                                            }
                                                            if (field === 'transplantDate') {
                                                                handleCellChange(label, year, 'transplanted', 'Yes');
                                                            }
                                                            if (field === 'harvestDate') {
                                                                handleCellChange(label, year, 'harvested', 'Yes');
                                                            }

                                                            const error = checkJournalRules(entry, field, isoDate);

                                                            if (error) {
                                                                showJournalMessage(rowKey, error);
                                                                return;
                                                            }

                                                            handleCellChange(label, year, field, isoDate);
                                                        }}
                                                        dateFormat="yyyy-MMM-dd"
                                                        placeholderText="Select date"
                                                        className="calendar-input"
                                                        title={journalFieldHints[field] || ''}
                                                        disabled={
                                                            disableRow
                                                        }
                                                        popperProps={{ strategy: "fixed" }}
                                                        popperPlacement="bottom-start"
                                                        
                                                        shouldCloseOnSelect={true}
                                                        closeOnScroll={true}

                                                        />
                                                ) : (
                                                    <textarea
                                                        className="gj-journal-cell-input"
                                                        value={value}
                                                            disabled={isPlantedField || isMethodField || isTransplantedField || isHarvestedField || disableRow}
                                                        onChange={(e) => {
                                                            const entry = activeMatrix[label][year];
                                                            const rowKey = `${label}|${year}`;
                                                            const newValue = e.target.value;

                                                            // RULE 2: if planted = "no", clear all fields
                                                            if (field === 'planted' && newValue.toLowerCase() === 'no') {
                                                                handleCellChange(label, year, 'plantingDate', null);
                                                                handleCellChange(label, year, 'method', '');
                                                                handleCellChange(label, year, 'amount', '');
                                                                handleCellChange(label, year, 'seedDepth', '');
                                                                handleCellChange(label, year, 'seedSpace', '');
                                                                handleCellChange(label, year, 'rowSpace', '');
                                                                handleCellChange(label, year, 'soilTemperature', '');
                                                                handleCellChange(label, year, 'dailyMinTemp', '');
                                                                handleCellChange(label, year, 'dailyMaxTemp', '');
                                                                handleCellChange(label, year, 'plantingNotes', '');
                                                                handleCellChange(label, year, 'germinationDate', null);
                                                                handleCellChange(label, year, 'germinationNotes', '');
                                                                handleCellChange(label, year, 'thinningDate', null);
                                                                handleCellChange(label, year, 'thinningOut', '');
                                                                handleCellChange(label, year, 'thinningNotes', '');
                                                                handleCellChange(label, year, 'transplanted', '');
                                                                handleCellChange(label, year, 'transplantDate', null);
                                                                handleCellChange(label, year, 'transplantAmount', '');
                                                                handleCellChange(label, year, 'transplantNote', '');
                                                                handleCellChange(label, year, 'harvested', '');
                                                                handleCellChange(label, year, 'harvestDate', null);
                                                                handleCellChange(label, year, 'harvestAmount', '');
                                                                handleCellChange(label, year, 'harvestNotes', '');
                                                            }



                                                            const error = checkJournalRules(entry, field, newValue);

                                                            if (error) {
                                                                showJournalMessage(rowKey, error);
                                                                return;
                                                            }

                                                            handleCellChange(label, year, field, e.target.value)
                                                        }}
                                                        readOnly={isYearView || parseInt(year) !== new Date().getFullYear()}
                                                        rows={1}
                                                        title={journalFieldHints[field] || ''}
                                                    />
                                                )}
                                            </div>
                                        );
                            })}
                        </div>
                     );
                })
            )}
            </div>
        </div>
      </div>
    </section>
);
};

export default GardenJournal;
